/** Public legal documents exposed by the site footer. */
export type LegalDocument = "notice" | "privacy" | "terms";

export type CommunityRouteTab = "pilots" | "setups" | "following";
export type LeaderboardRouteView = "times" | "pilots";
export const settingsRouteSections = ["profiles", "games", "devices", "security"] as const;
export type SettingsRouteSection = (typeof settingsRouteSections)[number];

/**
 * Languages that own a URL of their own.
 *
 * Declared here rather than imported from `../i18n` on purpose: this module is pulled in by
 * nearly every component and by the prerender script, and `../i18n` initialises i18next as a
 * module side effect. Keeping routing free of that keeps it cheap and unit-testable.
 * `i18n/index.ts` asserts the two lists agree.
 */
export type RouteLanguage = "fr" | "en";
export const DEFAULT_ROUTE_LANGUAGE: RouteLanguage = "fr";

/**
 * URL prefix per language. French is the default and carries **no** prefix, so `/pricing` is the
 * canonical French page and `/en/pricing` its English counterpart — the shape Google expects for
 * a site with a default language plus one translation.
 */
export const LOCALE_PREFIX: Record<RouteLanguage, string> = { fr: "", en: "/en" };

export type Route =
  | { name: "home" }
  | { name: "app-home" }
  | { name: "app-community"; tab: CommunityRouteTab }
  | { name: "app-setups" }
  | { name: "app-leaderboard"; view: LeaderboardRouteView }
  | { name: "app-leaderboard-compare" }
  | { name: "app-advice" }
  | { name: "app-ideas" }
  | { name: "app-fuel-calculator" }
  | { name: "app-sessions" }
  | { name: "app-session"; id: string }
  | { name: "app-profile" }
  | { name: "app-plans" }
  | { name: "app-settings"; section: SettingsRouteSection }
  | { name: "app-admin" }
  | { name: "login" }
  | { name: "register" }
  | { name: "verify-email"; token: string }
  | { name: "pricing" }
  | { name: "legal"; document: LegalDocument }
  | { name: "product" }
  /** `handle` is whatever the URL carried: the `@handle` normally, a legacy user id sometimes. */
  | { name: "user"; handle: string }
  /** No page answers this URL. nginx serves the prerendered 404 with a real 404 status. */
  | { name: "not-found" };

const withQuery = (path: string, query?: URLSearchParams): string => {
  const suffix = query?.toString();
  return suffix ? `${path}?${suffix}` : path;
};

/**
 * Canonical paths used by the frontend.
 *
 * Keeping them here prevents buttons and links from silently drifting away from
 * the parser when a section is renamed or moved.
 *
 * They are **language-neutral**: `localizedHref` adds the locale prefix where it matters (the
 * public pages a crawler reads). See {@link localizedHref}.
 */
export const routes = {
  home: "/",
  login: "/login",
  register: "/register",
  product: "/features",
  pricing: "/pricing",
  pricingFrom: (origin: string): string => `/pricing?from=${encodeURIComponent(origin)}`,
  verifyEmail: (token: string): string =>
    withQuery("/verify-email", new URLSearchParams({ token })),
  legalRoot: "/legal",
  legal: (document: LegalDocument): string => `/legal/${document}`,
  userRoot: "/u",
  /**
   * A public profile, addressed by `@handle`.
   *
   * The handle is what puts the pilot's name in the URL, where a cuid puts nothing — so callers
   * pass the handle. A user id still resolves (old links, and the rare row with no handle), and
   * the profile page rewrites the address bar to the canonical handle once it knows it.
   */
  user: (handle: string): string => `/u/${encodeURIComponent(handle)}`,
  app: {
    home: "/app",
    communityRoot: "/app/community",
    community: (tab: CommunityRouteTab = "pilots"): string => `/app/community/${tab}`,
    following: "/app/community/following",
    setups: "/app/setups",
    sessions: "/app/sessions",
    session: (id: string): string => `/app/sessions/${encodeURIComponent(id)}`,
    advice: "/app/advice",
    ideas: "/app/ideas",
    fuelCalculator: "/app/fuel-calculator",
    leaderboardRoot: "/app/leaderboard",
    leaderboard: (view: LeaderboardRouteView = "times"): string => `/app/leaderboard/${view}`,
    leaderboardCompare: (query?: URLSearchParams): string =>
      withQuery("/app/leaderboard/compare", query),
    profile: "/app/profile",
    plans: "/app/plans",
    settingsRoot: "/app/settings",
    settings: (section: SettingsRouteSection): string => `/app/settings/${section}`,
    admin: "/app/admin",
  },
} as const;

/** Bookmarks kept readable while their canonical destination evolves. */
const legacyRoutes = {
  appBrowse: "/app/browse",
  appDiscover: "/app/discover",
  appSubscriptions: "/app/subscriptions",
  appPilotRanking: "/app/pilot-ranking",
  appPlansInSettings: "/app/settings/plans",
  appBillingInSettings: "/app/settings/billing",
  account: "/account",
  discover: "/discover",
  admin: "/admin",
} as const;

const pathWithoutQuery = (path: string): string => path.split("?", 1)[0] ?? path;
const isPathAtOrBelow = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

const decodedSegmentAfter = (path: string, prefix: string): string | null => {
  if (!path.startsWith(`${prefix}/`)) return null;
  const encoded = path.slice(prefix.length + 1).split("/", 1)[0];
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
};

/**
 * Split a locale prefix off a pathname.
 *
 * `/en/pricing` → `{ language: "en", path: "/pricing" }`, `/pricing` → `{ language: null,
 * path: "/pricing" }`. A `null` language means the URL expressed no preference, which is not
 * the same as French: the caller then keeps whatever the visitor already had.
 */
export function splitLocale(pathname: string): { language: RouteLanguage | null; path: string } {
  for (const language of Object.keys(LOCALE_PREFIX) as RouteLanguage[]) {
    const prefix = LOCALE_PREFIX[language];
    if (!prefix) continue;
    if (pathname === prefix) return { language, path: "/" };
    if (pathname.startsWith(`${prefix}/`)) return { language, path: pathname.slice(prefix.length) };
  }
  return { language: null, path: pathname };
}

/**
 * Paths that exist in one language only, and must never get a locale prefix.
 *
 * A member profile shows setups, lap times and a bio — user-generated content that is not
 * translated. `/u/x` and `/en/u/x` would be two URLs for the same thing, doubling the crawl
 * surface of the site's largest page class to change nothing but the labels around it. One URL
 * per profile, and nginx redirects the prefixed form back to it.
 */
const LOCALE_NEUTRAL_PREFIXES = ["/u/"] as const;

export const isLocaleNeutralPath = (path: string): boolean =>
  LOCALE_NEUTRAL_PREFIXES.some((prefix) => path.startsWith(prefix));

/**
 * Prefix a language-neutral path for `language`.
 *
 * Used by the public pages — the ones a crawler reads — so an English page links to English
 * pages and the `hreflang` cluster stays self-consistent. App pages are `noindex` and behind a
 * login, so they keep neutral hrefs and rely on the stored preference.
 */
export function localizedHref(path: string, language: RouteLanguage): string {
  const prefix = LOCALE_PREFIX[language];
  if (!prefix) return path;
  if (path === "/") return prefix;
  if (isLocaleNeutralPath(path)) return path;
  return `${prefix}${path}`;
}

/** Resolve a browser path (with optional query string) into the page rendered by Root. */
export function routeFromPath(pathname: string, search = ""): Route {
  const { path: withLocaleStripped } = splitLocale(pathWithoutQuery(pathname || routes.home));
  // A trailing slash is the same page — `/pricing/` must not fall through to the home page.
  const path =
    withLocaleStripped.length > 1 && withLocaleStripped.endsWith("/")
      ? withLocaleStripped.replace(/\/+$/, "")
      : withLocaleStripped;
  const query = search.startsWith("?") ? search.slice(1) : search;

  // The root, explicitly: it used to fall through to the catch-all, which now answers "not found".
  if (path === "/") return { name: "home" };

  // Connected app sections (longest and most specific prefixes first).
  if (path === routes.app.community("pilots")) return { name: "app-community", tab: "pilots" };
  if (path === routes.app.community("setups")) return { name: "app-community", tab: "setups" };
  if (path === routes.app.following) return { name: "app-community", tab: "following" };
  if (isPathAtOrBelow(path, routes.app.communityRoot)) return { name: "app-community", tab: "pilots" };
  if (isPathAtOrBelow(path, legacyRoutes.appSubscriptions)) {
    return { name: "app-community", tab: "following" };
  }
  if (isPathAtOrBelow(path, legacyRoutes.appBrowse)) return { name: "app-community", tab: "setups" };
  if (isPathAtOrBelow(path, routes.app.setups)) return { name: "app-setups" };
  if (isPathAtOrBelow(path, legacyRoutes.appPilotRanking)) {
    return { name: "app-leaderboard", view: "pilots" };
  }
  if (isPathAtOrBelow(path, routes.app.advice)) return { name: "app-advice" };
  if (isPathAtOrBelow(path, routes.app.ideas)) return { name: "app-ideas" };
  if (isPathAtOrBelow(path, routes.app.fuelCalculator)) return { name: "app-fuel-calculator" };
  if (isPathAtOrBelow(path, routes.app.leaderboardCompare())) {
    return { name: "app-leaderboard-compare" };
  }
  if (isPathAtOrBelow(path, routes.app.leaderboard("pilots"))) {
    return { name: "app-leaderboard", view: "pilots" };
  }
  if (isPathAtOrBelow(path, routes.app.leaderboardRoot)) {
    return { name: "app-leaderboard", view: "times" };
  }
  const sessionId = decodedSegmentAfter(path, routes.app.sessions);
  if (sessionId) return { name: "app-session", id: sessionId };
  if (isPathAtOrBelow(path, routes.app.sessions)) return { name: "app-sessions" };
  if (isPathAtOrBelow(path, legacyRoutes.appDiscover)) return { name: "app-community", tab: "pilots" };
  if (isPathAtOrBelow(path, routes.app.profile)) return { name: "app-profile" };
  if (
    isPathAtOrBelow(path, routes.app.plans) ||
    isPathAtOrBelow(path, legacyRoutes.appPlansInSettings) ||
    isPathAtOrBelow(path, legacyRoutes.appBillingInSettings)
  ) {
    return { name: "app-plans" };
  }
  if (isPathAtOrBelow(path, routes.app.settingsRoot)) {
    const candidate = decodedSegmentAfter(path, routes.app.settingsRoot);
    return {
      name: "app-settings",
      section: settingsRouteSections.find((section) => section === candidate) ?? "profiles",
    };
  }
  if (isPathAtOrBelow(path, routes.app.admin)) return { name: "app-admin" };
  // The dashboard is the default destination, including unknown legacy app paths.
  if (isPathAtOrBelow(path, routes.app.home)) return { name: "app-home" };

  // Legacy top-level routes.
  if (isPathAtOrBelow(path, legacyRoutes.account)) return { name: "app-profile" };
  if (isPathAtOrBelow(path, legacyRoutes.discover)) return { name: "app-community", tab: "pilots" };
  if (isPathAtOrBelow(path, legacyRoutes.admin)) return { name: "app-admin" };
  if (isPathAtOrBelow(path, routes.register)) return { name: "register" };
  if (isPathAtOrBelow(path, routes.login)) return { name: "login" };
  if (isPathAtOrBelow(path, "/verify-email")) {
    return { name: "verify-email", token: new URLSearchParams(query).get("token") ?? "" };
  }
  const legalDocument = decodedSegmentAfter(path, routes.legalRoot);
  if (legalDocument === "notice" || legalDocument === "privacy" || legalDocument === "terms") {
    return { name: "legal", document: legalDocument };
  }
  // Exact `/legal` only: `/legal/whatever` is a missing document, not the legal notice under
  // another name — serving the notice there would be a soft 404.
  if (path === routes.legalRoot) return { name: "legal", document: "notice" };
  if (isPathAtOrBelow(path, routes.pricing)) return { name: "pricing" };
  if (isPathAtOrBelow(path, routes.product)) return { name: "product" };
  const userHandle = decodedSegmentAfter(path, routes.userRoot);
  if (userHandle) return { name: "user", handle: userHandle };
  // Nothing claimed this path. Answering with the home page instead would be a soft 404: a 200
  // for a URL that does not exist, which Search Console flags and which wastes crawl budget on
  // every typo and dead link pointed at the site.
  return { name: "not-found" };
}

/**
 * Translate a pre-History-API bookmark into its path equivalent.
 *
 * The app shipped with hash routing (`#/pricing`), so those links exist in bookmarks, in
 * verification e-mails and wherever members pasted them. Returning a path here lets the boot
 * sequence rewrite the URL instead of dropping the visitor on the home page. `null` means the
 * hash was not one of ours and must be left alone (in-page anchors, for one).
 */
export function pathFromLegacyHash(hash: string): string | null {
  if (!hash.startsWith("#/")) return null;
  const withoutHash = hash.slice(1);
  const [rawPath = "/", rawQuery] = withoutHash.split("?", 2);
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, "") : rawPath;
  return rawQuery ? `${path}?${rawQuery}` : path;
}
